import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import {
  Box,
  Alert,
  Button,
  Grid,
  TextField,
  Typography,
  Divider,
  Chip,
  IconButton,
  CircularProgress,
  MenuItem,
  Tooltip,
} from '@mui/material'
import { Delete, Verified } from '@mui/icons-material'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useAppDispatch, useAppSelector } from '../../hooks/reduxHooks'
import {
  doGetPatientInsHistory,
  doSavePatientInsHistory,
  setInsError,
  setInsInfo,
} from '../../state/reducers/patientsReducer'
import {
  PatientInsHistory,
  mapInsiResponseToHistory,
  InsiService,
  InsVerificationResponseDTO,
  VerifyInsiDTO,
} from '../../services/InsiService'
import moment from 'moment'
import { CreatePatientDTO } from '@/services/dtos'
import { CouvertureDto, PatientReadSourceEnum } from '../../common/interfaces'
import { SexEnum } from '@services/api'
import { enqueueSnackbar } from 'notistack'
import { CircleCheck } from 'lucide-react'
import { doCreatePatient } from '@state/thunks/patientsThunk'
import { CustomDateInput } from '../inputs/CustomDateInput'
import { ActiveFormType } from './ManagePatientComponent'

type Props = {
  shouldUpdate?: boolean
  getPatientValues: (
    ignoreValidation: boolean,
  ) => Promise<CreatePatientDTO | null>
  getCoverageValues: (shouldValidate?: boolean) => Promise<CouvertureDto | null>
  patientId?: number
  setActiveForm: (form: ActiveFormType) => void
  isOpen: boolean
}

type OtherFirstNameField = {
  value: string
}

export type InsiFormValues = {
  lastName: string
  firstName: string
  otherFirstNames: OtherFirstNameField[]
  sex: string
  birthDate: string
  birthPlace: string
  ins: string
  cleIns: string
  oidIns: string
  rangNaissance?: string
}

const emptyFormValues: InsiFormValues = {
  lastName: '',
  firstName: '',
  otherFirstNames: [],
  sex: '',
  birthDate: '',
  birthPlace: '',
  ins: '',
  cleIns: '',
  oidIns: '',
}

const TEMPORARY_INSI_OID = '1.2.250.1.213.1.4.9'
const TEMPORARY_INSI_MESSAGE = 'L\'INS retourné est temporaire.'

const mergePatientDefaults = (
  current: Partial<InsiFormValues>,
  incoming: Partial<InsiFormValues>,
): Partial<InsiFormValues> => ({
  lastName: current.lastName || incoming.lastName || '',
  firstName: current.firstName || incoming.firstName || '',
  otherFirstNames:
    current.otherFirstNames && current.otherFirstNames.length > 0
      ? current.otherFirstNames
      : incoming.otherFirstNames || [],
  sex: current.sex || incoming.sex || '',
  birthDate: current.birthDate || incoming.birthDate || '',
  birthPlace: current.birthPlace || incoming.birthPlace || '',
  ins: current.ins || incoming.ins || '',
  cleIns: current.cleIns || incoming.cleIns || '',
  oidIns: current.oidIns || incoming.oidIns || '',
})

export interface PatientInsiInfosRef {
  getValues: () => Promise<InsiFormValues>
  setValues: (values: Partial<InsiFormValues>) => void
}

const PatientInsiInfos = forwardRef<PatientInsiInfosRef, Props>(
  ({ getPatientValues, getCoverageValues, patientId, isOpen }, ref) => {
    const dispatch = useAppDispatch()
    const [isEditing, setIsEditing] = useState(false)
    const [patientDefaults, setPatientDefaults] =
      useState<Partial<InsiFormValues>>(emptyFormValues)
    const [isSaving, setIsSaving] = useState(false)
    const [isVerifying, setIsVerifying] = useState(false)
    const [verificationResult, setVerificationResult] =
      useState<InsVerificationResponseDTO | null>(null)

    const {
      patientInsHistory,
      selectedInsiSearchResult,
      memberSituation,
      insError,
      insInfo,
    } = useAppSelector((state) => ({
      patientInsHistory: state.patients.patientInsHistory,
      selectedInsiSearchResult: state.patients.selectedInsiSearchResult,
      memberSituation: state.order.selectedMemberSituation,
      insError: state.patients.insError,
      insInfo: state.patients.insInfo,
    }))

    const resolvedDefaultValues = useMemo<InsiFormValues>(() => {
      const base: InsiFormValues = {
        ...emptyFormValues,
        ...patientDefaults,
        otherFirstNames:
          patientDefaults.otherFirstNames?.map((item) => ({
            value: item?.value || '',
          })) || [],
      }

      const currentResult = selectedInsiSearchResult?.message
      if (currentResult) {
        const prenoms =
          currentResult.listePrenoms
            ?.split(',')
            .map((value) => value.trim())
            .filter(Boolean) || []
        const [primaryFirstName, ...restFirstNames] =
          prenoms.length > 0 ? prenoms : [currentResult.prenom || '']

        return {
          ...base,
          lastName: currentResult.nomNaissance || base.lastName,
          firstName: primaryFirstName || base.firstName,
          otherFirstNames: restFirstNames.map((name) => ({ value: name })),
          sex: currentResult.sexe || base.sex,
          birthDate: currentResult.dateNaissance
            ? moment(currentResult.dateNaissance, 'YYYY-MM-DD').format(
                'DD/MM/YYYY',
              )
            : moment(base.birthDate).format('DD/MM/YYYY'),
          birthPlace: currentResult.lieuNaissance || base.birthPlace,
          ins: currentResult.ins || base.ins,
          cleIns: currentResult.cleIns || base.cleIns,
          oidIns: currentResult.oidIns || base.oidIns,
        }
      }

      if (patientInsHistory?.ins_value) {
        const baseIns = patientInsHistory.ins_value.slice(0, 13) || ''
        const cle = patientInsHistory.ins_value.slice(13) || ''

        return {
          ...base,
          ins: baseIns,
          cleIns: cle,
          oidIns: patientInsHistory.ins_oid || base.oidIns,
        }
      }

      return base
    }, [patientDefaults, patientInsHistory, selectedInsiSearchResult])

    const {
      control,
      reset,
      setValue,
      getValues,
      watch,
      setError: setFormError,
      formState: { errors },
    } = useForm<InsiFormValues>({
      defaultValues: resolvedDefaultValues,
    })

    useImperativeHandle(ref, () => ({
      getValues: async () => getValues(),
      setValues: (values: Partial<InsiFormValues>) => {
        const currentValues = getValues()
        const nextValues: InsiFormValues = {
          ...emptyFormValues,
          ...currentValues,
          ...values,
          otherFirstNames:
            values.otherFirstNames?.map((item) => ({
              value: item?.value || '',
            })) ||
            currentValues.otherFirstNames ||
            [],
        }

        reset(nextValues)
        setPatientDefaults((prev) => mergePatientDefaults(prev, nextValues))
      },
    }))

    const birthDate = watch('birthDate')

    useEffect(() => {
      if (patientId) {
        dispatch(doGetPatientInsHistory(patientId))
      }
    }, [patientId, dispatch])

    useEffect(() => {
      if (!isOpen) {
        return
      }
      if (!getPatientValues) {
        setPatientDefaults(emptyFormValues)
        return
      }

      getPatientValues(true)
        .then(async (patientValues) => {
          if (!patientValues) {
            return
          }
          const formCurrentValues = getValues()
          const coverageValues = await getCoverageValues(false)
          const getBeneficiareDateNasissance = (value: string) => {
            return (
              value.slice(6, 8) +
              '/' +
              value.slice(4, 6) +
              '/' +
              value.slice(0, 4)
            )
          }

          const birthDate = coverageValues?.beneficaireDateNaissance
            ? getBeneficiareDateNasissance(
                coverageValues.beneficaireDateNaissance,
              )
            : formCurrentValues.birthDate ||
              (patientValues.birthDate
                ? moment(patientValues.birthDate).format('DD/MM/YYYY')
                : patientValues.birthDateStr
                ? moment(patientValues.birthDateStr, 'DD-MM-YYYY').format(
                    'DD/MM/YYYY',
                  )
                : '')
          const derivedDefaults: Partial<InsiFormValues> = {
            firstName:
              formCurrentValues.firstName || patientValues.firstName || '',
            lastName:
              formCurrentValues.lastName ||
              coverageValues?.beneficiaireNom ||
              '',
            sex:
              patientValues.sex === SexEnum.M
                ? 'M'
                : patientValues.sex === SexEnum.F
                ? 'F'
                : '',
            birthDate,
            otherFirstNames: [],
            birthPlace:
              formCurrentValues.birthPlace || patientValues.placeOfBirth || '',
            cleIns: formCurrentValues.cleIns || '',
            ins: formCurrentValues.ins || '',
            oidIns: formCurrentValues.oidIns || '',
          }

          setPatientDefaults((prev) =>
            mergePatientDefaults(prev, derivedDefaults),
          )
        })
        .catch(() => {
          setPatientDefaults(emptyFormValues)
        })
    }, [getPatientValues, patientId, isOpen])

    const sanitize = (value?: string | number | null) =>
      value !== undefined && value !== null
        ? String(value).replace(/\s/g, '')
        : ''

    const getBirthDate = () => {
      if (birthDate) {
        const parsed = moment(birthDate, 'DD/MM/YYYY')
        return parsed.format('YYYY-MM-DD')
      }
      return ''
    }

    const handleVerify = async (oidOverride?: string) => {
      if (isVerifying) {
        return
      }
      dispatch(setInsInfo(null))
      setVerificationResult(null)

      if (!memberSituation) {
        dispatch(
          setInsError(
            'Veuillez sélectionner un médecin disposant d\'une situation valide avant de vérifier l\'INS.',
          ),
        )
        return
      }

      const formValues = getValues()
      if (!formValues) {
        dispatch(
          setInsError(
            'Veuillez compléter les informations du patient avant la vérification INSi.',
          ),
        )
        return
      }

      const birthDate = getBirthDate()
      if (!birthDate) {
        dispatch(
          setInsError('La date de naissance est requise pour vérifier l\'INS.'),
        )
        return
      }

      const oid =
        oidOverride ||
        patientInsHistory?.ins_oid ||
        selectedInsiSearchResult?.message.oidIns ||
        formValues.oidIns

      if (!oid) {
        dispatch(setInsError('L\'OID est requis pour vérifier l\'INS.'))
        return
      }

      const psModel = {
        psNumeroFacturation:
          memberSituation.situation.numIdentificationPSFacturation,
        psIdentStructure: memberSituation.situation.numIdentificationStructure,
        authentificationMode: parseInt(
          process.env.REACT_APP_AUTH_MODE || '1',
          10,
        ),
        codePorteurCPS: '0',
      }
      const coverageValues = await getCoverageValues(false)
      if (!formValues.ins) {
        dispatch(setInsError('L\'INS est requis pour vérifier l\'INS.'))
        return
      }
      const cleIns =
        formValues.cleIns || selectedInsiSearchResult?.message.cleIns
      if (!cleIns) {
        dispatch(setInsError('La clé INS est requise pour vérifier l\'INS.'))
        return
      }
      const insWithKey = `${formValues.ins}${cleIns}`
      const isApcv = coverageValues?.readSource === PatientReadSourceEnum.Apcv

      const verifyPayload: VerifyInsiDTO = {
        psModel,
        checkModel: {
          assureNumSecu: sanitize(coverageValues?.assureNir),
          beneficiaireNom: formValues.lastName || '',
          beneficiaireNomUsage: formValues.lastName || '',
          beneficiairePrenom: formValues.firstName || '',
          beneficiaireSexe: formValues.sex || '',
          beneficiaireLieuNaissance: formValues.birthPlace || '',
          beneficiaireDateNaissance: birthDate,
          beneficiaireRang: '1',
          beneficiaireNumSecu: sanitize(coverageValues?.beneficiaireNir || ''),
          numeroSerieCV: '',
          oid,
          insaVerifier: sanitize(insWithKey),
        },
        fichierInjecte: {
          fichierBase64: '',
        },
        assertions: {
          assertionPs: '',
          assertionVitale: '',
          cpsBase64AuthCertificate: '',
          cpsBase64SignatureCertificate: '',
        },
        idJfse: memberSituation.situation.idJfse,
        isApcv,
      }

      try {
        setIsVerifying(true)
        const response = await InsiService.verifyIns(verifyPayload)

        const msg = response?.message?.reponse?.erreurMessage
        const erreur = response.message?.reponse?.erreur
        if (erreur) {
          dispatch(setInsError(msg))
          return
        }
        setVerificationResult(response)
        handleSave()
      } catch (error: any) {
        dispatch(
          setInsError(error?.message || 'Erreur lors de la vérification INSi.'),
        )
      } finally {
        setIsVerifying(false)
      }
    }

    const handleSave = async () => {
      dispatch(setInsError(null))

      let _patientId = patientId
      if (!_patientId) {
        const values = getValues()
        const birthDate = new Date(values.birthDate)
        const sex = values.sex
        const placeOfBirth = values.birthPlace

        const createdPatient = await dispatch(
          doCreatePatient({
            firstName: values.firstName,
            lastName: values.lastName,
            birthDate: birthDate,
            sex: sex || SexEnum.M,
            placeOfBirth: placeOfBirth,
            motherMaidenName: '',
          } as CreatePatientDTO),
        ).unwrap()
        _patientId = createdPatient.id
      }

      if (!selectedInsiSearchResult) {
        // dispatch(setInsError('Aucun résultat de recherche à sauvegarder.'))
        return
      }

      setIsSaving(true)
      try {
        const mappedHistory = mapInsiResponseToHistory(
          selectedInsiSearchResult,
          _patientId,
        )

        await dispatch(
          doSavePatientInsHistory({
            patientId: _patientId,
            insHistory: mappedHistory,
          }),
        ).unwrap()
        await dispatch(doGetPatientInsHistory(_patientId)).unwrap()

        enqueueSnackbar({
          message: 'INS sauvegardé avec succès.',
          variant: 'success',
        })
      } catch (error: any) {
        dispatch(
          setInsError(
            error?.message || 'Impossible de sauvegarder l\'INS pour le moment.',
          ),
        )
      } finally {
        setIsSaving(false)
      }
    }

    const mappedInsiData = useMemo<PatientInsHistory | null>(() => {
      if (selectedInsiSearchResult && patientId) {
        return mapInsiResponseToHistory(selectedInsiSearchResult, patientId)
      }
      return null
    }, [selectedInsiSearchResult, patientId])

    const {
      fields: otherFirstNameFields,
      append,
      remove,
    } = useFieldArray({
      name: 'otherFirstNames',
      control,
    })

    useEffect(() => {
      reset(resolvedDefaultValues)
    }, [resolvedDefaultValues, reset])

    useEffect(() => {
      const selectedOid = selectedInsiSearchResult?.message?.oidIns
      const oid = selectedOid
      if (oid === TEMPORARY_INSI_OID) {
        dispatch(setInsInfo(TEMPORARY_INSI_MESSAGE))
      } else if (insInfo === TEMPORARY_INSI_MESSAGE) {
        dispatch(setInsInfo(null))
      }
    }, [dispatch, insInfo, selectedInsiSearchResult?.message?.oidIns])

    const historyEntries = useMemo(() => {
      if (selectedInsiSearchResult?.message?.historique?.length) {
        return selectedInsiSearchResult.message.historique
      }
      if (mappedInsiData?.insHistory?.length) {
        return mappedInsiData.insHistory
      }
      return patientInsHistory?.insHistory || []
    }, [
      mappedInsiData?.insHistory,
      patientInsHistory?.insHistory,
      selectedInsiSearchResult?.message?.historique,
    ])

    const isInsVerified = !!patientInsHistory
    const readOnly = isInsVerified

    const readOnlyFieldSx = readOnly
      ? {
          '& .MuiInputBase-root': {
            bgcolor: '#EBEBEB',
          },
        }
      : undefined

    const isIdentifiersReadonly = !!selectedInsiSearchResult || readOnly

    const handleBirthDateValidate = (isValid: boolean) => {
      if (!isValid) {
        setFormError('birthDate', {
          message: 'Date de naissance invalide',
        })
      } else {
        setFormError('birthDate', {
          message: '',
        })
      }
    }

    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">Informations INS</Typography>
          {!readOnly && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={() => handleVerify()}
                startIcon={
                  isVerifying ? (
                    <CircularProgress size={18} />
                  ) : (
                    <Verified fontSize="small" />
                  )
                }
                disabled={isVerifying}
              >
                {isVerifying ? 'Vérification…' : 'Vérifier'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => setIsEditing((prev) => !prev)}
                disabled={
                  (!selectedInsiSearchResult && !patientInsHistory) ||
                  isInsVerified
                }
              >
                {isEditing ? 'Désactiver l\'édition' : 'Modifier'}
              </Button>
            </Box>
          )}
        </Box>

        {!selectedInsiSearchResult && !patientInsHistory && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Aucune information INS. Lancez une vérification INSi pour récupérer
            les données, puis sauvegardez-les.
          </Alert>
        )}

        {insError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {insError}
          </Alert>
        )}

        {insInfo && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {insInfo}
          </Alert>
        )}

        {verificationResult && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {verificationResult.message.reponse.erreurMessage}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Nom de naissance"
                  fullWidth
                  InputProps={{ readOnly }}
                  sx={readOnlyFieldSx}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Prénom"
                  fullWidth
                  InputProps={{ readOnly }}
                  sx={readOnlyFieldSx}
                />
              )}
            />
          </Grid>

          {otherFirstNameFields.map((field, index) => (
            <Grid item xs={12} md={4} key={field.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Controller
                  name={`otherFirstNames.${index}.value`}
                  control={control}
                  render={({ field: arrayField }) => (
                    <TextField
                      {...arrayField}
                      label={`Autre prénom ${index + 1}`}
                      fullWidth
                      InputProps={{ readOnly }}
                      sx={readOnlyFieldSx}
                    />
                  )}
                />
                {isEditing && (
                  <IconButton
                    aria-label="Supprimer le prénom"
                    onClick={() => remove(index)}
                    size="small"
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Grid>
          ))}

          {isEditing && (
            <Grid item xs={12}>
              <Button variant="text" onClick={() => append({ value: '' })}>
                Ajouter un prénom
              </Button>
            </Grid>
          )}

          <Grid item xs={12} md={4}>
            <Controller
              name="sex"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Sexe"
                  fullWidth
                  disabled={readOnly}
                  sx={readOnlyFieldSx}
                >
                  <MenuItem value="M">Homme (M)</MenuItem>
                  <MenuItem value="F">Femme (F)</MenuItem>
                </TextField>
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="birthDate"
              control={control}
              render={() => (
                <CustomDateInput
                  value={birthDate}
                  onDateChange={(value) => setValue('birthDate', value)}
                  label="Date de naissance"
                  onValidate={handleBirthDateValidate}
                  error={errors.birthDate?.message}
                  disabled={readOnly}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="birthPlace"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Lieu de naissance"
                  fullWidth
                  InputProps={{ readOnly }}
                  sx={readOnlyFieldSx}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4} />

          <Grid item xs={12} md={2}>
            <Controller
              name="cleIns"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value || ''}
                  onChange={(event) => {
                    const sanitized = (event.target.value || '')
                      .replace(/\s/g, '')
                      .slice(0, 2)
                    field.onChange(sanitized)
                  }}
                  label="Clé INS"
                  fullWidth
                  inputProps={{ maxLength: 2 }}
                  InputProps={{ readOnly: isIdentifiersReadonly }}
                  sx={readOnlyFieldSx}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="ins"
              control={control}
              render={({ field }) => (
                <TextField
                  name={field.name}
                  inputRef={field.ref}
                  onBlur={field.onBlur}
                  value={field.value || ''}
                  onChange={(event) => {
                    const rawValue = event.target.value || ''
                    const sanitized = rawValue.replace(/\s/g, '')
                    const insPart = sanitized.slice(0, 13)
                    const clePart = sanitized.slice(13, 15)
                    field.onChange(insPart)
                    if (clePart) {
                      setValue('cleIns', clePart, {
                        shouldDirty: true,
                        shouldTouch: true,
                      })
                    }
                  }}
                  label="INS"
                  fullWidth
                  InputProps={{ readOnly: isIdentifiersReadonly }}
                  sx={readOnlyFieldSx}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="oidIns"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="OID INS"
                  fullWidth
                  InputProps={{ readOnly: isIdentifiersReadonly }}
                  sx={readOnlyFieldSx}
                />
              )}
            />
          </Grid>
          {isInsVerified && (
            <Tooltip title="INSi vérifié">
              <CircleCheck size={40} color="green" className="mt-6 ml-2" />
            </Tooltip>
          )}
        </Grid>

        {!!historyEntries.length && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }}>
              <Chip label="Historique INS" size="small" />
            </Divider>
            <Grid container spacing={2}>
              {historyEntries.map((entry, index) => (
                <Grid item xs={12} sm={6} md={4} key={`ins-history-${index}`}>
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      INS: {entry.insi}
                    </Typography>
                    <Typography variant="body2">OID: {entry.oid}</Typography>
                    <Typography variant="body2">
                      Date de fin:{' '}
                      {entry.dateFin
                        ? moment(entry.dateFin).format('DD/MM/YYYY')
                        : 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Box>
    )
  },
)

export default PatientInsiInfos
